<template>
  <modal
    :id="modalId"
    class="add-contributor"
    :title="$t('actor_contributors')"
    is-simplified-modal
    is-closeable
    close-on-blur
    @close="close"
  >
    <template v-slot:body>
      <div class="add-contributor__body">
        <p class="add-contributor__intro">{{ $t('actor_contributors_subtitle') }}</p>

        <!-- Stays in view while the contributor list underneath scrolls -->
        <form class="add-contributor__invite" @submit.prevent="invite">
          <!--
            Bottom-aligned so the button lines up with the field rather than with the caption
            above it. No built-in button size is the field's 36px (extra-small is 24, small 30,
            the default 38), so the height is set on the button below.
          -->
          <div class="tw-flex items-end gap-2">
            <!-- The label wraps the input rather than pointing at it: DsInput renders a wrapper
                 div, so an `id` handed to it would land on the div and not on the field. -->
            <label class="add-contributor__field flex-1 min-w-0">
              <span class="add-contributor__label">{{ $t('actor_contributors_invite') }}</span>
              <DsInput
                v-model="email"
                type="email"
                icon="email"
                is-simplified
                autofocus
                :placeholder="$t('actor_contributors_email')"
                @pressedEnter="invite"
              />
            </label>

            <DsButton
              class="add-contributor__invite-button shrink-0"
              variant="rounded"
              size="small"
              :label="inviting ? $t('loading') : $t('actor_contributors_invite_contributor')"
              :disabled="!email.trim() || inviting"
              @click="invite"
            />
          </div>

          <!-- The same switch the rows use: it sets the same attribute, so it should not be a
               checkbox here and a toggle six lines down. -->
          <div class="add-contributor__contactable-field tw-flex items-center gap-2">
            <ToggleButton
              :enabled="canBeContacted"
              @update:modelValue="canBeContacted = $event"
            />
            <span class="add-contributor__contactable-field-label">
              {{ $t('edit_actor_segment_allow_this_contributor_to_be_contacted') }}
            </span>
          </div>

          <p v-if="error" class="add-contributor__error">{{ error }}</p>
          <p v-else-if="message" class="add-contributor__message">{{ message }}</p>
        </form>

        <div class="add-contributor__list-section">
          <div v-if="loading" class="tw-flex items-center justify-center py-10">
            <Loading/>
          </div>

          <div v-else-if="!contributors.length" class="add-contributor__empty text-center">
            <p class="text-sm font-semibold text-gray-700 mb-1">
              {{ $t('actor_contributors_empty_title') }}
            </p>
            <p class="text-sm text-gray-400 m-0">
              {{ $t('actor_contributors_empty_subtitle') }}
            </p>
          </div>

          <template v-else>
            <p class="add-contributor__section-label">{{ $t('actor_contributors_current') }}</p>

            <ul class="add-contributor__list tw-flex flex-col gap-2">
              <li
                class="add-contributor__row tw-flex items-center gap-3"
                v-for="contributor in contributors"
                :key="contributor.user_id"
              >
                <span class="add-contributor__avatar shrink-0" :style="avatarStyle(contributor)">
                  <span v-if="!contributor.photo_url">{{ initials(contributor.user_name) }}</span>
                </span>

                <span class="tw-flex flex-col flex-1 min-w-0">
                  <!-- A contributor invited by email has no name until they register, so the
                       email carries the row on its own in that case. -->
                  <span
                    class="text-sm font-semibold text-gray-900 truncate"
                    v-if="contributor.user_name"
                  >{{ contributor.user_name }}</span>
                  <span class="text-xs text-gray-500 truncate">{{ contributor.user_email }}</span>
                </span>

                <!--
                  Whether this person may be messaged through the platform. The platform's own
                  switch (ToggleButton, over @vueform/toggle) rather than a bespoke control, so it
                  reads the same as every other on/off in the product; the caption beside it says
                  what is being switched, since the switch itself is only 52px of track.
                -->
                <span
                  class="add-contributor__contactable tw-flex items-center gap-2 shrink-0"
                  v-if="canManageContactable"
                >
                  <span class="add-contributor__contactable-label">
                    {{ $t('actor_contributors_contactable') }}
                  </span>
                  <!--
                    Not `v-model`: the write has to reach the server before it counts, so the row
                    is flipped in `toggleContactable` and put back if the request is refused. The
                    switch renders from `enabled`, so it follows both.
                  -->
                  <ToggleButton
                    :enabled="contributor.allow_conversation"
                    @update:modelValue="toggleContactable(contributor, $event)"
                  />
                </span>
                <!-- Everyone else sees the state without the control. -->
                <span
                  class="add-contributor__badge shrink-0"
                  v-else-if="contributor.allow_conversation"
                >{{ $t('actor_contributors_contactable') }}</span>

                <!--
                  Owners are listed but not removable here: this modal's delete endpoint only
                  matches contributor claims (DeleteContributorRequest::claimExists), and removing
                  an owner claim un-claims the profile — which the classic profile already does,
                  behind a confirmation. The badge is what says why the row has no bin.
                -->
                <span class="add-contributor__badge shrink-0" v-if="isActorOwner(contributor)">
                  {{ $t('actor_contributors_role_owner') }}
                </span>
                <button
                  class="add-contributor__remove shrink-0"
                  type="button"
                  v-else-if="canRemoveContributors"
                  :aria-label="$t('actor_contributors_remove')"
                  v-tooltip.left="$t('actor_contributors_remove')"
                  @click="remove(contributor)"
                >
                  <Icon name="trash" size="14"/>
                </button>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <!-- extra-small is exactly the 24px the footer's 49px band leaves after its padding. -->
      <DsButton
        variant="rounded"
        size="extra-small"
        :label="$t('actor_contributors_done')"
        @click="close"
      />
    </template>
  </modal>
</template>

<script>
  import { defineComponent } from 'vue'

  import Modal from './Modal.vue'
  import ToggleButton from '../Form/ToggleButton.vue'
  import DsInput from '../Form/DsInput.vue'
  import DsButton from '../DsButton/DsButton.vue'
  import Icon from '../Icon/Icon.vue'
  import Loading from '../Dashboard/ConceptMap/Loading.vue'

  import MODAL_IDS from '../../constants/modal-ids.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui.js'
  import { fetchContributors, inviteContributor, removeContributor, updateContactableContributors } from '../../api/actors.js'

  export default defineComponent({
    name: 'AddContributorModal',
    components: { Modal, ToggleButton, DsInput, DsButton, Icon, Loading },
    emits: ['close'],
    data () {
      return {
        modalId: MODAL_IDS.ADD_CONTRIBUTOR_MODAL,
        email: '',
        canBeContacted: true,
        contributors: [],
        loading: false,
        inviting: false,
        error: '',
        message: '',
      }
    },
    computed: {
      /**
       * The actor being looked at, not `$route.params.id`: this modal is opened from the actor
       * detail pages, and two of them also render embedded in a spotting area or community page
       * whose route parameter is that parent's id rather than the actor's.
       */
      actorId () {
        return this.$store.getters.detailActor.id || this.$route.params.id
      },
      /**
       * Mirrors DeleteContributorRequest::authorize: the ecosystem team, the actor's owner and
       * the actor's own contributors. Deliberately not portfolio members — the invite endpoint
       * lets them in with `allowInviteContributors`, the delete endpoint does not, so a remove
       * button for them would only 403.
       *
       * Owner rows are excluded at the row level instead (see the template): they are in the
       * list now, and an owner claim needs the claim endpoint rather than this one.
       */
      canRemoveContributors () {
        return this.$store.getters.isMember || this.userIsActorTeam
      },
      /**
       * Mirrors UpdateContactableContributorsRequest::authorize: the ecosystem team always, and
       * the actor's own team only on a public ecosystem — being messageable through the platform
       * is a publisher feature, so on a private ecosystem there is nothing for them to manage.
       *
       * Stricter than the endpoint in one way: it accepts any claim, this asks for a verified
       * one. An unverified claim is not yet the actor's team.
       */
      canManageContactable () {
        return this.$store.getters.isMember ||
          (this.$store.getters.isPublic && this.userIsActorTeam)
      },
      /**
       * A verified owner or contributor claim on this actor. Read off the profile with a guard
       * rather than through CompanyMixin's `userIsActorOwner`, which indexes into
       * `state.user.profile.claims` unguarded.
       */
      userIsActorTeam () {
        const claims = (this.$store.state.user.profile && this.$store.state.user.profile.claims) || []

        return claims.some(claim => claim.id === this.actorId &&
          claim.claim_verified &&
          ['actor-owner', 'actor-contributor'].includes(claim.claim_type))
      },
    },
    methods: {
      initials (name) {
        if (!name) {
          return '?'
        }

        return name.split(' ').slice(0, 2).map(part => part[0]).join('').toUpperCase()
      },
      isActorOwner (contributor) {
        return contributor.claim_type === 'actor-owner'
      },
      avatarStyle (contributor) {
        return contributor.photo_url ? { backgroundImage: `url(${contributor.photo_url})` } : {}
      },
      /**
       * The endpoint answers 200 with a *message* whether or not the claim was created — already
       * being a contributor is reported that way rather than as an error — and `id` is the
       * existing user either way. So there is nothing in the response that separates the two, and
       * its message is shown as a plain notice rather than dressed up as a success.
       */
      invite () {
        const email = this.email.trim()

        if (!email || this.inviting) {
          return
        }

        this.inviting = true
        this.error = ''
        this.message = ''

        inviteContributor({ email, actor_id: this.actorId })
          .then(response => {
            this.message = response.message || ''
            this.email = ''

            if (!response.id || !this.canBeContacted) {
              return
            }

            const contactableIds = this.contributors
              .filter(contributor => contributor.allow_conversation)
              .map(contributor => contributor.user_id)

            // Returned into the chain rather than fired and forgotten: the refresh below used to
            // race this write and usually won, so someone invited as contactable came back from
            // the server not contactable until the modal was reopened.
            //
            // Its own catch, so a refused write is reported without skipping the refresh — the
            // invite itself has already gone through by this point.
            return updateContactableContributors(this.actorId, [...contactableIds, response.id])
              .catch(this.handleError)
          })
          .then(() => {
            this.$bus.emit('updateContributors')

            return this.fetch()
          })
          .catch(this.handleError)
          .finally(() => {
            this.inviting = false
          })
      },
      remove (contributor) {
        this.error = ''
        this.message = ''

        removeContributor({ user_id: contributor.user_id, actorId: this.actorId })
          .then(response => {
            this.message = (response && response.message) || ''

            this.$bus.emit('updateContributors')
            this.fetch()
          })
          .catch(this.handleError)
      },
      /**
       * The endpoint takes the complete set of contactable contributors rather than one change,
       * so the row is set locally first and the whole list is derived from that. Putting it back
       * is the only way to undo a rejected write, since there is nothing to re-read that would
       * not also throw away the rest of the list's state.
       *
       * `next` is the switch's own new value rather than `!previous`: the control is the source
       * of truth for what was asked for, and a revert then cannot drift out of step with it.
       */
      toggleContactable (contributor, next) {
        const previous = contributor.allow_conversation

        contributor.allow_conversation = next
        this.error = ''
        this.message = ''

        const contactableIds = this.contributors
          .filter(entry => entry.allow_conversation)
          .map(entry => entry.user_id)

        updateContactableContributors(this.actorId, contactableIds)
          .then(() => {
            this.$bus.emit('updateContributors')
          })
          .catch(errors => {
            contributor.allow_conversation = previous

            this.handleError(errors)
          })
      },
      /**
       * Returns its promise so callers can refresh *after* their own write has landed.
       *
       * `includeOwners: true`, like every other actor call site. It used to ask for contributors
       * alone, which made the contactable write lossy: updateContactableClaims takes the complete
       * desired set — it switches `allow_conversation` off for every claim on the actor, owners
       * included, then back on for the ids it was handed — so a set derived from a list without
       * the owners in it silently un-contacted them on every toggle.
       */
      fetch () {
        this.loading = true

        return fetchContributors(this.actorId, { includeOwners: true })
          .then(list => {
            this.contributors = list || []
          })
          .catch(this.handleError)
          .finally(() => {
            this.loading = false
          })
      },
      /**
       * Errors used to be collected onto a local object that nothing rendered, so a rejected
       * invite was silently swallowed. The rejection carries either a general `message` or
       * Laravel's per-field `errors`, and the email field is the only one this form has.
       */
      handleError (errors) {
        const fieldErrors = (errors && (errors.errors || {})).email || (errors && errors.email)

        if (Array.isArray(fieldErrors) && fieldErrors.length) {
          this.error = fieldErrors[0]

          return
        }

        if (typeof fieldErrors === 'string') {
          this.error = fieldErrors

          return
        }

        if (errors && errors.message === 'Unexpected error') {
          this.error = 'You can only add contributors to published profiles.'

          return
        }

        this.error = (errors && errors.message) || 'Something went wrong, please try again.'
      },
      close () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, this.modalId)
        this.$emit('close')
      },
    },
    mounted () {
      this.fetch()
    },
  })
</script>

<style lang="scss" scoped>
  // The simplified modal strips the padding from its container and scrolls the body itself, so the
  // spacing of the sections below is what gives the modal its gutters.
  .add-contributor__intro {
    margin: 0;
    padding: 16px 20px 0;
    font-size: 0.8rem;
    line-height: 1.4;
    color: #6b7280;
  }

  .add-contributor__invite {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 12px 20px 14px;
    background: white;
    border-bottom: 1px solid #f3f4f6;
  }

  // A <label> is inline by default, and this one wraps a block caption and DsInput's wrapper div.
  .add-contributor__field {
    display: block;
  }

  /**
   * Matches DsInput's 36px so the two line up on the row. `.button--small` is 30px
   * (line-height 24 + 4px top padding + 2px borders), so height, line-height and padding are all
   * restated here; the 2px top padding keeps the design system's optical nudge for uppercase
   * labels, scaled to this height. Doubled-up class so it beats `.button--small`'s own
   * line-height whichever order the stylesheets land in, and `box-sizing: border-box` is global
   * so the 36px includes the borders.
   */
  .add-contributor__invite .add-contributor__invite-button {
    height: 36px;
    line-height: 32px;
    padding: 2px 16px 0;
    margin: 0;
  }

  .add-contributor__label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  .add-contributor__contactable-field {
    margin-top: 12px;
  }

  .add-contributor__contactable-field-label {
    font-size: 0.78rem;
    line-height: 1.3;
    color: #6b7280;
  }

  .add-contributor__list-section {
    padding: 14px 20px 18px;
    min-height: 150px;
  }

  .add-contributor__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .add-contributor__section-label {
    margin: 0 0 8px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  .add-contributor__empty {
    padding: 32px 20px;
    border: 1px dashed #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
  }

  .add-contributor__error {
    margin: 12px 0 0;
    font-size: 0.8rem;
    color: #dc2626;
  }

  // Deliberately neutral rather than green: see the note on invite() — the endpoint reports
  // "already a contributor" through the same 200 and the same message field as a real invite.
  .add-contributor__message {
    margin: 12px 0 0;
    font-size: 0.8rem;
    color: #4b5563;
  }

  .add-contributor__row {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:hover {
      border-color: #d1d5db;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }
  }

  .add-contributor__avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid #f3f4f6;
    background-color: #f9fafb;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 600;
    color: #6b7280;
  }

  // The switch's own 52x24 comes from @vueform/toggle's default theme, which is what every other
  // toggle in the product renders at; only the caption next to it belongs to this modal.
  .add-contributor__contactable-label {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #9ca3af;
    white-space: nowrap;
  }

  .add-contributor__badge {
    padding: 3px 8px;
    border-radius: 999px;
    background: #eef2f7;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #6b7280;
    white-space: nowrap;
  }

  .add-contributor__remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid #e5e7eb;
    border-radius: 50%;
    background: white;
    color: #9ca3af;
    cursor: pointer;
    transition: color 0.15s ease-in-out, border-color 0.15s ease-in-out;

    &:hover {
      color: #dc2626;
      border-color: #fecaca;
    }
  }
</style>
